// src/components/profile/ProfileJoinCta.tsx
//
// Guest-only signup strip for public profile pages. Server component: the
// page decides (from the server session) whether to render it — this is a
// marketing module, NOT content gating (gating stays server-side in the API).
//
// Design notes (DESIGN.md): this is the page's deliberate brand moment — the
// ribbon hairline + the one sanctioned heading gradient — and the page's
// closing ask, rendered after the neighbor mesh so guests leave on a signup
// prompt instead of a link grid.

import Link from 'next/link';

export default function ProfileJoinCta({
  displayName,
  returnTo,
  heading,
  body,
}: {
  displayName?: string;
  returnTo: string;
  /** Override the profile-specific heading (e.g. leaderboards: "Get verified. Get ranked."). */
  heading?: string;
  body?: string;
}) {
  const name = (displayName || '').replace(/\s+/g, ' ').trim();
  const first = name.split(' ')[0] || 'them';
  const href = `/register?returnTo=${encodeURIComponent(returnTo)}`;
  const headingText = heading || `Work with ${first} on JuicyStarz`;
  const bodyText = body ||
    `JuicyStarz is where adult-industry talent, producers, and crew connect. Join free to message ${first}, see full photos, and post casting calls.`;

  return (
    <section aria-label="Join JuicyStarz" className="container mx-auto px-4 md:px-6 mt-12 mb-4">
      <div className="mx-auto max-w-xl text-center rounded-2xl border border-border bg-card text-card-foreground shadow-sm px-6 py-10">
        <div className="juicy-rule mx-auto mb-4" aria-hidden />
        <h2 className="font-js juicy-title text-3xl md:text-4xl font-extrabold leading-[1.15]">
          {headingText}
        </h2>
        <p className="mt-3 text-sm md:text-base text-muted-foreground">
          {bodyText}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            href={href}
            className="inline-flex h-11 items-center justify-center rounded-md bg-[hsl(var(--primary))] px-6 text-sm font-semibold text-[hsl(var(--primary-foreground))] shadow-xs transition-colors hover:bg-[color-mix(in_oklab,hsl(var(--primary))_92%,black_8%)]"
          >
            Join free
          </Link>
          <Link
            href={`/login?returnTo=${encodeURIComponent(returnTo)}`}
            className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Already a member? Sign in
          </Link>
        </div>
      </div>
    </section>
  );
}
